/**
 * @file   src\components\Onboarding\Dashboard\LeftpanelTabContent.js
 * @brief  This file is responsible for general tab functionality
 * @date   April, 2023
 * @author ZCO Engineer
 * @copyright (c) 2023, ZCO
 */
import React, { useEffect, useState, useContext, useRef } from "react";
import FilterCategory from "./FilterCategory";
import FilterLocationSearch from "../FilterLocationSearch";
import FilterKeywordSearch from "../FilterKeywordSearch";
import Accordion from "react-bootstrap/Accordion";
import { filterContext } from "../../../../pages/v2/Home";
import {
  accsPvtBthRmchkBxFilter,
  adaAccessibleChkBx,
  alwPetsChkBx,
  BUY,
  DEFAULT_MAX_BUDGET_BUY,
  DEFAULT_MAX_BUDGET_LTR,
  DEFAULT_MAX_BUDGET_STR,
  DEFAULT_STEP_VALUE_BUY,
  DEFAULT_STEP_VALUE_LTR,
  DEFAULT_STEP_VALUE_STR,
  INDEX_TO_BE_SLICE_15,
  LONG_RENT,
  pvtEntrnceChkBx,
} from "../../../../constants/common";
import { getPropertyTypes } from "../../../../services/commonServices";
import { getSquareFeetList } from "../../../../services/wantAdService";
import { handleRadioButonChange } from "../../../../actions/common";
import RangeSliderComponent from "../../../Common/RangeSlider";
import SelectDropDown from "../../../Common/SelectDropDown";
import {
  convertToNumber,
  handleNegativeInput,
} from "../../../../helpers/validations";
import TextBox from "../../../Common/TextBox";
import {
  replaceComma,
  getLocalStorage,
  replaceString,
  thousandSeperatedValue,
  thousandSeperatedValueFilter,
  getPriceText,
  checkIsNumber,
  arraySplice,
  filterArrayBasedOnKey,
} from "../../../../helpers/common";
import DateRangeSelector from "../../../DateRangeSelector";
import { usePrevious } from "../../../../hooks/usePrevious";
import PremiumPlanModal from "../../../../components/PlanUpgrade/PremiumPlanModal";
import { useUserDetails } from "../../../../hooks/useUserDetails";

const LeftpanelTabContent = (props) => {
  const {
    updateFilter,
    filterQuery,
    setUserLocation,
    key,
    gitchaNotAvailble,
    searchedLocation,
    checkGitchaAvailableInLoc,
    userLocation,
  } = props;

  const {
    isPremiumUser,
    isAgent,
    userDetails: userDetailsFromHook,
    loadUserData,
    isUserLoggedIn,
  } = useUserDetails();

  const filterQueryFromStorage = getLocalStorage("filterQuery");
  const [propertyList, setPropertyList] = useState([]);
  const [typeOfPlaceList, setTypeOfPlaceList] = useState([]);
  const [selectedTypeOfPlace, setSelectedTypeOfPlace] = useState([]);
  const [budgetRange, setBudgetRange] = useState("");
  const [minError, setMinError] = useState("");
  const [maxError, setMaxError] = useState("");
  const [squareFootageMinValue, setSquareFootageMinValue] = useState(null);
  const [squareFootageMaxValue, setSquareFootageMaxValue] = useState(null);
  const filterCntxt = useContext(filterContext);
  const [propertyCount, setPropertyCount] = useState(0);
  const [typeOfPlaceCount, setTPCount] = useState(0);
  const { adType, clearFilterQuery } = filterCntxt;
  const [isPrivateRoomSelected, setIsPrivateRoomSelected] = useState(false);
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [squareFeetMinList, setSqareFeetMinList] = useState([]);
  const [squareFeetMaxList, setSqareFeetMaxList] = useState([]);
  const [isAgentOnly, setIsAgentOnly] = useState(false);

  const [showPremiumPlanModal, setPremiumPlanModal] = useState(false);
  const PremiumPlanModalClose = () => setPremiumPlanModal(false);
  const PremiumPlanModalShow = () => setPremiumPlanModal(true);

  const MIN_SF_DROPDOWN = "minSFDrop";
  const MAX_SF_DROPDOWN = "maxSFDrop";
  const minRef = useRef(null);
  const maxRef = useRef(null);
  let DEFAULT_MAX_BUDGET;
  const DEFAULT_MIN_BUDGET = 0;
  let DEFAULT_STEP_VALUE;

  if (adType === BUY) {
    DEFAULT_MAX_BUDGET = DEFAULT_MAX_BUDGET_BUY;
    DEFAULT_STEP_VALUE = DEFAULT_STEP_VALUE_BUY;
  } else if (adType === LONG_RENT) {
    DEFAULT_MAX_BUDGET = DEFAULT_MAX_BUDGET_LTR;
    DEFAULT_STEP_VALUE = DEFAULT_STEP_VALUE_LTR;
  } else {
    DEFAULT_MAX_BUDGET = DEFAULT_MAX_BUDGET_STR;
    DEFAULT_STEP_VALUE = DEFAULT_STEP_VALUE_STR;
  }

  const budgetMin = _.get(filterQueryFromStorage, "priceMin");
  const budgetMax = _.get(filterQueryFromStorage, "priceMax");
  const STR_OPTION_DETAILS_PVT_ROOM = [
    { id: adaAccessibleChkBx, text: "ADA Accessible" },
    { id: alwPetsChkBx, text: "Allows pets" },
    { id: accsPvtBthRmchkBxFilter, text: "Access to private bathroom" },
    { id: pvtEntrnceChkBx, text: "Private Entrance" },
  ];

  const STR_OPTION_DETAILS_ALL = [
    { id: adaAccessibleChkBx, text: "ADA Accessible" },
    { id: alwPetsChkBx, text: "Allows pets" },
  ];

  // const handleAgentOnlyChange = (isChecked) => {
  //   if (isAgent && !isPremiumUser && isChecked) {
  //     PremiumPlanModalShow();
  //     return;
  //   }

  //   setIsAgentOnly(isChecked);
  //   updateFilter({ agentOnly: isChecked });
  // };

  useEffect(() => {
    setIsAgentOnly(false);
  }, [adType]);

  useEffect(() => {
    if (adType === BUY) {
      getPropertyTypes(adType)
        .then((response) => {
          if (response.errorCode === 0 && _.get(response, "result", null)) {
            const propertyTypes = _.get(response, "result", null);
            setPropertyList(propertyTypes);
          }
        })
        .catch((err) => {
          console.log("Error", err);
        });
    } else {
      setPropertyList([]);
    }

    let DEFAULT_BUDGET_RANGE = [DEFAULT_MIN_BUDGET, DEFAULT_MAX_BUDGET];
    if (budgetMin && !budgetMax) {
      DEFAULT_BUDGET_RANGE = [budgetMin, DEFAULT_MAX_BUDGET];
    } else if (!budgetMin && budgetMax) {
      DEFAULT_BUDGET_RANGE = [DEFAULT_MIN_BUDGET, budgetMax];
    } else if (budgetMin && budgetMax) {
      DEFAULT_BUDGET_RANGE = [budgetMin, budgetMax];
    }
    setBudgetRange(DEFAULT_BUDGET_RANGE);
    setSelectedTypeOfPlace(
      _.get(filterQueryFromStorage, "typeOfPlace") || [10, 11, 6],
    );
    getSquareFeetList()
      .then((response) => {
        if (response.errorCode === 0 && _.get(response, "result", null)) {
          const list = _.get(response, "result", null);
          let newList = [];
          list.map((lst) => {
            const value = _.get(lst, "Sqft_Value");
            const text = _.get(lst, "Sqft_Value_Text", "");
            newList.push({ value: value, label: text });
          });

          setSqareFeetMinList([{ value: null, label: "No min" }, ...newList]);
          setSqareFeetMaxList([{ value: null, label: "No max" }, ...newList]);
          const maxValue =
            _.get(filterQueryFromStorage, "sqrftMax") != null
              ? {
                  value: _.get(filterQueryFromStorage, "sqrftMax", null),
                  label: _.get(filterQueryFromStorage, "sqrftMax"),
                }
              : { value: null, label: "No max" };
          const minValue =
            _.get(filterQueryFromStorage, "sqrftMin") != null
              ? {
                  value: _.get(filterQueryFromStorage, "sqrftMin", null),
                  label: _.get(filterQueryFromStorage, "sqrftMin"),
                }
              : { value: null, label: "No min" };
          setSquareFootageMaxValue(maxValue);
          setSquareFootageMinValue(minValue);
          setStartDate(_.get(filterQueryFromStorage, "fromDate") || null);
          setEndDate(_.get(filterQueryFromStorage, "toDate") || null);
        }
      })
      .catch((err) => {
        console.log("Error", err);
      });
  }, [adType]);

  useEffect(() => {
    if (adType !== BUY) {
      let type = "";
      if (
        selectedTypeOfPlace.includes(11) &&
        selectedTypeOfPlace.includes(10)
      ) {
        type = "es-pr";
      } else if (
        selectedTypeOfPlace.includes(11) &&
        selectedTypeOfPlace.includes(6)
      ) {
        type = "pr-land";
      } else if (
        selectedTypeOfPlace.includes(10) &&
        selectedTypeOfPlace.includes(6)
      ) {
        type = "es-land";
      }
      if (
        selectedTypeOfPlace.includes(11) &&
        selectedTypeOfPlace.includes(10) &&
        selectedTypeOfPlace.includes(6)
      ) {
        type = "all";
      }
      if (selectedTypeOfPlace.length == 1 && selectedTypeOfPlace.includes(11)) {
        type = "privateroom";
      }
      if (selectedTypeOfPlace.length == 1 && selectedTypeOfPlace.includes(10)) {
        type = "entirespace";
      }
      if (selectedTypeOfPlace.length == 1 && selectedTypeOfPlace.includes(6)) {
        type = "land";
      } else {
        setPropertyList([]);
      }
      getPropertyTypes(type)
        .then((response) => {
          if (response.errorCode === 0 && _.get(response, "result", null)) {
            const propertyTypes = _.get(response, "result", null);
            setPropertyList(propertyTypes);
          }
        })
        .catch((err) => {
          console.log("Error", err);
        });

      if (selectedTypeOfPlace.includes(11)) {
        setIsPrivateRoomSelected(true);
      } else {
        setIsPrivateRoomSelected(false);
      }
    }
  }, [selectedTypeOfPlace]);

  const prevMinBdgt = usePrevious(budgetRange[0]);
  const prevMaxBdgt = usePrevious(budgetRange[1]);

  useEffect(() => {
    updateRangeSliders();
  }, [startDate, endDate]);

  useEffect(() => {
    if (prevMinBdgt && prevMinBdgt !== budgetRange[0]) {
      updateFilter({ priceMin: budgetRange[0] });
    }
  }, [budgetRange[0]]);

  useEffect(() => {
    const mxBdgt = budgetRange[1] === DEFAULT_MAX_BUDGET ? "" : budgetRange[1];
    if (prevMaxBdgt && prevMaxBdgt !== budgetRange[1]) {
      updateFilter({ priceMin: budgetMin, priceMax: mxBdgt });
    }
  }, [budgetRange[1]]);

  const updateRangeSliders = async () => {
    updateFilter({ fromDate: startDate, toDate: endDate });
  };

  useEffect(() => {
    if (adType !== BUY) {
      ///      For loading type of place
      getPropertyTypes("rent")
        .then((response) => {
          if (response.errorCode === 0 && _.get(response, "result", null)) {
            const propertyTypes = _.get(response, "result", null);
            setTypeOfPlaceList(propertyTypes);
          }
        })
        .catch((err) => {
          console.log("Error", err);
        });
    }
  }, [adType]);
  /**
   * To handle clear filter
   */
  const clearFilter = () => {
    clearFilterQuery();
    setSquareFootageMinValue({ value: null, label: "No min" });
    setSquareFootageMaxValue({ value: null, label: "No max" });
    setBudgetRange([DEFAULT_MIN_BUDGET, DEFAULT_MAX_BUDGET]);
    setPropertyCount(0);
    setTPCount(0);
  };

  const getSelectedItems = (property) => {
    const selectedItemArray = _.get(filterQuery, property, []);
    return selectedItemArray;
  };
  /**
   * To handle property selection
   * @param {*} e
   */
  const handlePropertyClick = (e) => {
    const value = convertToNumber(handleRadioButonChange(e));
    const selectedItemArray = getSelectedItems("propertyType");
    if (value) {
      let itemArray;
      if (_.includes(selectedItemArray, value)) {
        itemArray = selectedItemArray.filter((e) => e !== value);
      } else {
        itemArray = [...selectedItemArray, value];
      }
      updateFilter({ propertyType: itemArray });
      setPropertyCount(itemArray.length);
    }
  };

  const handleAdditionalRequirementsClick = (e, typeId) => {
    const selectedItemArray = getSelectedItems(e.target.value);
    if (selectedItemArray == 0 || selectedItemArray == null) {
      updateFilter({ [typeId]: 1 });
    } else {
      updateFilter({ [typeId]: 0 });
    }
  };
  /**
   * To handle type of place selection
   * @param {*} e
   */
  const handleTypeOfPlaceClick = (e) => {
    const selectedItemArray = getSelectedItems("typeOfPlace");
    const value = convertToNumber(handleRadioButonChange(e));
    if (value) {
      let itemArray;
      if (_.includes(selectedItemArray, value)) {
        itemArray = selectedItemArray.filter((e) => e !== value);
      } else {
        itemArray = [...selectedItemArray, value];
      }
      setSelectedTypeOfPlace(itemArray);
      if (itemArray.includes(11)) {
        setIsPrivateRoomSelected(true);
      } else {
        setIsPrivateRoomSelected(false);
      }

      updateFilter({ typeOfPlace: itemArray });
      setTPCount(itemArray.length);
    }
  };
  /**
   * To load property types
   * @returns
   */
  const loadPropertyMain = () => {
    const selectedItemArray = getSelectedItems("propertyType");
    const List =
      propertyList &&
      propertyList.map((pt) => {
        const typeId = _.get(pt, "PropertyTypeId", "");
        const typeText = _.get(pt, "PropertyTypeText", "");
        const isChecked =
          selectedItemArray && _.includes(selectedItemArray, typeId);
        return (
          <label
            key={typeId}
            className="d-block mb-2 form-check-label checkbox-label"
            htmlFor={typeId}
          >
            <input
              className="form-check-input"
              type="checkbox"
              name="chkType"
              value={typeId ? typeId : ""}
              id={typeId}
              checked={isChecked}
              onChange={(e) => handlePropertyClick(e)}
            />
            {typeText}
          </label>
        );
      });

    return List;
  };
  /**
   * To load additional requirements section
   * @returns
   */
  const loadAdditionalRequirements = () => {
    const options = isPrivateRoomSelected
      ? STR_OPTION_DETAILS_PVT_ROOM
      : STR_OPTION_DETAILS_ALL;

    const List =
      options &&
      options.map((pt) => {
        const typeId = _.get(pt, "id", "");
        const typeText = _.get(pt, "text", "");
        const isChecked = getSelectedItems(typeId);
        return (
          <label
            key={typeId}
            className="d-block mb-2 form-check-label checkbox-label"
            htmlFor={typeId}
          >
            <input
              className="form-check-input"
              type="checkbox"
              name="chkType"
              value={typeId ? typeId : ""}
              id={typeId}
              checked={isChecked == 1 ? true : false}
              onChange={(e) => handleAdditionalRequirementsClick(e, typeId)}
            />
            {typeText}
          </label>
        );
      });

    return List;
  };

  /**
   * To load types of place view
   * @returns
   */
  const loadTypeOfPlace = () => {
    const selectedItemArray = getSelectedItems("typeOfPlace");
    const List =
      typeOfPlaceList &&
      typeOfPlaceList.map((pt) => {
        const typeId = _.get(pt, "PropertyTypeId", "");
        const typeText = _.get(pt, "PropertyTypeText", "");
        const isChecked =
          selectedItemArray && _.includes(selectedItemArray, typeId);
        return (
          <label
            key={typeId}
            className="d-block mb-2 form-check-label checkbox-label"
            htmlFor={typeId}
          >
            <input
              className="form-check-input"
              name="chkPlace"
              type="checkbox"
              value={typeId ? typeId : ""}
              id={typeId}
              checked={isChecked}
              onChange={(e) => handleTypeOfPlaceClick(e)}
            />
            {typeText}
          </label>
        );
      });

    return List;
  };

  const handleKeyWordSearch = (e) => {
    updateFilter({ search: e.target.value });
  };

  const handleBudgetMinChange = (e) => {
    const budgetMax = budgetRange && budgetRange[1];
    const value = handleNegativeInput(e.target.value);
    if (value) {
      setBudgetRange([parseInt(replaceComma(value)), budgetMax]);
    } else {
      setBudgetRange([null, budgetMax]);
    }
  };

  const handleBudgetMaxChange = (e) => {
    const budgetMin = budgetRange && budgetRange[0];
    const value = handleNegativeInput(e.target.value);
    if (parseInt(replaceComma(value))) {
      setBudgetRange([budgetMin, parseInt(replaceComma(value))]);
    } else {
      setBudgetRange([budgetMin, null]);
    }
  };

  const handleSquareFootMinChange = (e) => {
    const value = _.get(e, "value", "");
    setSquareFootageMinValue(e);
    updateFilter({ sqrftMin: value });
  };

  const handleSquareFootMaxChange = (e) => {
    const value = _.get(e, "value", "");
    setSquareFootageMaxValue(e);
    updateFilter({ sqrftMax: value });
  };

  /**
   * To handle number of bedrooms selection
   * @returns
   */
  const handleBedRoomClick = (e) => {
    const noOfBedrooms = getSelectedItems("bedrooms");
    const value = handleRadioButonChange(e);
    const selectedValue = replaceString(value, "+");
    if (selectedValue == noOfBedrooms) {
      updateFilter({ bedrooms: null });
    } else {
      updateFilter({ bedrooms: selectedValue });
    }
  };
  /**
   * To handle number of beds selection
   * @returns
   */
  const handleBedsClick = (e) => {
    const noOfBeds = getSelectedItems("beds");
    const value = handleRadioButonChange(e);
    const selectedValue = replaceString(value, "+");
    if (selectedValue == noOfBeds) {
      updateFilter({ beds: null });
    } else {
      updateFilter({ beds: selectedValue });
    }
  };
  /**
   * To handle number of bathrooms selection
   * @returns
   */
  const handleBathRoomClick = (e) => {
    const noOfBathrooms = getSelectedItems("baths");
    const value = handleRadioButonChange(e);
    const selectedValue = replaceString(value, "+");
    if (selectedValue == noOfBathrooms) {
      updateFilter({ baths: null });
    } else {
      updateFilter({ baths: selectedValue });
    }
  };

  const addNewOptionsToMinList = (list, newOption) => {
    const customList = buildCustomList(list, newOption);
    setSqareFeetMinList(customList);
  };

  const addNewOptionsToMaxList = (list, newOption) => {
    const customList = buildCustomList(list, newOption);
    setSqareFeetMaxList(customList);
  };

  const buildCustomList = (list, newOption) => {
    const filteredArray = filterArrayBasedOnKey({
      list: list,
      filterKey: "value",
      value: _.get(newOption, "value", null),
    });
    let customOptions = [...list];
    if (filteredArray?.length === 0 && _.get(newOption, "value", null)) {
      customOptions = [...list, newOption];
    }
    return customOptions;
  };

  const handleOnInputChange = (e, dropSection) => {
    if (dropSection === MIN_SF_DROPDOWN) {
      if (minRef.current && minRef.current.contains(document.activeElement)) {
        if (checkIsNumber(e)) {
          const options = { value: convertToNumber(e), label: e };
          addNewOptionsToMinList(
            arraySplice(squareFeetMinList, INDEX_TO_BE_SLICE_15),
            options,
          );
          setSquareFootageMinValue(options);
          updateFilter({ sqrftMin: e });
        }
      }
    } else {
      if (maxRef.current && maxRef.current.contains(document.activeElement)) {
        if (checkIsNumber(e)) {
          const options = { value: convertToNumber(e), label: e };
          addNewOptionsToMaxList(
            arraySplice(squareFeetMaxList, INDEX_TO_BE_SLICE_15),
            options,
          );
          setSquareFootageMaxValue(options);
          updateFilter({ sqrftMax: e });
        }
      }
    }
  };

  /**
   * To handle number of bedrooms selection view
   * @returns
   */
  const loadBedRooms = () => {
    const items = ["1+", "2+", "3+", "4+", "5+"];
    const noOfBedrooms = getSelectedItems("bedrooms");
    const list =
      items &&
      items.map((itm, index) => {
        const itmValue = replaceString(itm, "+");
        return (
          <button
            type="button"
            value={itmValue}
            className={
              noOfBedrooms === itmValue
                ? "btn btn-primary active"
                : "btn btn-primary"
            }
            key={index}
            onClick={(e) => handleBedRoomClick(e)}
          >
            {itm}
          </button>
        );
      });
    return list;
  };
  /**
   * To handle number of beds selection view
   * @returns
   */
  const loadBeds = () => {
    const items = ["1+", "2+", "3+", "4+", "5+"];
    const noOfBedrooms = getSelectedItems("beds");
    const list =
      items &&
      items.map((itm, index) => {
        const itmValue = replaceString(itm, "+");
        return (
          <button
            type="button"
            value={itmValue}
            className={
              noOfBedrooms === itmValue
                ? "btn btn-primary active"
                : "btn btn-primary"
            }
            key={index}
            onClick={(e) => handleBedsClick(e)}
          >
            {itm}
          </button>
        );
      });
    return list;
  };
  /**
   * To handle number of bathrooms selection view
   * @returns
   */
  const loadBathRooms = () => {
    const items = ["1+", "2+", "3+", "4+", "5+"];
    const noOfBathrooms = getSelectedItems("baths");
    const list =
      items &&
      items.map((itm, index) => {
        const itmValue = replaceString(itm, "+");
        return (
          <button
            type="button"
            value={itmValue}
            className={
              noOfBathrooms === itmValue
                ? "btn btn-primary active"
                : "btn btn-primary"
            }
            key={index}
            onClick={(e) => handleBathRoomClick(e)}
          >
            {itm}
          </button>
        );
      });
    return list;
  };

  const updateReloadParamAfterPlanChange = () => {
    loadUserData();
  };

  return (
    <>
      <FilterCategory />

      {userLocation !== null && (
        <>
          <FilterLocationSearch
            updateFilter={updateFilter}
            adType={adType}
            setUserLocation={setUserLocation}
            key={key}
            gitchaNotAvailble={gitchaNotAvailble}
            searchedLocation={searchedLocation}
            checkGitchaAvailableInLoc={checkGitchaAvailableInLoc}
          />
          <FilterKeywordSearch
            handleSearch={handleKeyWordSearch}
            keyWord={getSelectedItems("search")}
          />
        </>
      )}

      {/* {isUserLoggedIn && isAgent && (
        <div className="filter-toggle-btn d-flex align-items-center">
          <div>
            <input
              type="checkbox"
              id="toggle-btn"
              className="toggle-btn"
              checked={isAgentOnly}
              onChange={(e) => handleAgentOnlyChange(e.target.checked)}
            />
            <label htmlFor="toggle-btn"></label>
          </div>
          <div>
            <span className="agent-feed">Agents only feed</span>
          </div>
        </div>
      )} */}

      <Accordion
        defaultActiveKey="2"
        alwaysOpen
        className={userLocation !== null ? "mt-4" : ""}
      >
        {adType !== BUY && (
          <Accordion.Item eventKey="0">
            <Accordion.Header>
              Type of place{" "}
              {getSelectedItems("typeOfPlace").length > 0
                ? `(${getSelectedItems("typeOfPlace").length})`
                : ""}
            </Accordion.Header>
            <Accordion.Body className="pt-0">
              <div className="field-wrap mt-0 mb-0">{loadTypeOfPlace()}</div>
            </Accordion.Body>
          </Accordion.Item>
        )}

        <Accordion.Item eventKey="1">
          <Accordion.Header>
            Property type{" "}
            {getSelectedItems("propertyType").length > 0
              ? `(${getSelectedItems("propertyType").length})`
              : ""}
          </Accordion.Header>
          <Accordion.Body className="pt-0">
            <div className="field-wrap mt-0 mb-0">{loadPropertyMain()}</div>
          </Accordion.Body>
        </Accordion.Item>
        <Accordion.Item eventKey="2">
          <Accordion.Header>{getPriceText(adType)}</Accordion.Header>
          <Accordion.Body className="pt-0">
            <div className="slider-sec">
              <div className="pb-4 pt-3">
                <RangeSliderComponent
                  min={DEFAULT_MIN_BUDGET}
                  max={DEFAULT_MAX_BUDGET}
                  value={
                    budgetRange && [
                      budgetRange[0] == null
                        ? DEFAULT_MIN_BUDGET
                        : budgetRange[0],
                      budgetRange[1] == null
                        ? DEFAULT_MAX_BUDGET
                        : budgetRange[1],
                    ]
                  }
                  stepValue={DEFAULT_STEP_VALUE}
                  onInput={setBudgetRange}
                />
              </div>
              <ul className="range-values-list rs-pb-37">
                <li className="position-relative">
                  <span className="dollar-sign-icon">$</span>
                  <label className="d-none"></label>

                  <TextBox
                    type="text"
                    className="form-control pad-left-25"
                    errorMessage={minError}
                    value={
                      budgetRange && budgetRange[0] === ""
                        ? ""
                        : thousandSeperatedValueFilter(budgetRange[0])
                    }
                    placeholder="0"
                    onChange={(e) => {
                      handleBudgetMinChange(e);
                    }}
                  ></TextBox>
                  <div className="error-msg"></div>
                </li>
                <li className="text-center">
                  <span className="middle-separator">-</span>
                </li>
                <li className="position-relative">
                  <span className="dollar-sign-icon">$</span>
                  <label className="d-none"></label>

                  <TextBox
                    type="text"
                    className="form-control pad-left-25"
                    errorMessage={maxError}
                    value={
                      budgetRange && budgetRange[1] === DEFAULT_MAX_BUDGET
                        ? "No max"
                        : thousandSeperatedValue(budgetRange[1])
                    }
                    placeholder="No max"
                    onChange={(e) => {
                      handleBudgetMaxChange(e);
                    }}
                  ></TextBox>
                </li>
              </ul>
            </div>
          </Accordion.Body>
        </Accordion.Item>
        {adType === "STR" && (
          <Accordion.Item eventKey="7">
            <Accordion.Header>Dates</Accordion.Header>
            <Accordion.Body className="pt-0">
              <DateRangeSelector
                startDate={startDate}
                setStartDate={setStartDate}
                endDate={endDate}
                setEndDate={setEndDate}
              />
            </Accordion.Body>
          </Accordion.Item>
        )}
        <Accordion.Item eventKey="3">
          <Accordion.Header>
            {adType === "STR" ? "Rooms and beds" : "Beds and baths"}
          </Accordion.Header>
          <Accordion.Body className="pt-0">
            <div className="lp-btn-group-wrap">
              <label className="form-label">
                {adType === "STR" ? "Bedrooms" : "Beds"}
              </label>
              <div
                className="btn-group"
                role="group"
                aria-label="Basic example"
              >
                {loadBedRooms()}
              </div>
            </div>

            {adType === "STR" && isPrivateRoomSelected && (
              <div className="lp-btn-group-wrap">
                <label className="form-label">Beds</label>
                <div
                  className="btn-group"
                  role="group"
                  aria-label="Basic example"
                >
                  {loadBeds()}
                </div>
              </div>
            )}

            <div className="lp-btn-group-wrap mb-3">
              <label className="form-label">Baths</label>
              <div
                className="btn-group"
                role="group"
                aria-label="Basic example"
              >
                {loadBathRooms()}
              </div>
            </div>
          </Accordion.Body>
        </Accordion.Item>

        {adType !== "STR" && (
          <Accordion.Item eventKey="4">
            <Accordion.Header>Square footage</Accordion.Header>
            <Accordion.Body className="pt-0">
              <div className="slider-sec">
                <ul className="range-values-list list-pad-fix rs-pb-37">
                  <li className="position-relative">
                    <label className="d-none"></label>
                    <div ref={minRef}>
                      <SelectDropDown
                        id={MIN_SF_DROPDOWN}
                        name="colors33"
                        options={squareFeetMinList}
                        className="lotsize-select"
                        classNamePrefix="select"
                        value={squareFootageMinValue}
                        onChange={(e) => handleSquareFootMinChange(e)}
                        onInputChange={(e) =>
                          handleOnInputChange(e, MIN_SF_DROPDOWN)
                        }
                        menuPlacement="top"
                        styles={{
                          dropdownIndicator: (provided, state) => ({
                            ...provided,
                            transform:
                              state.selectProps.menuIsOpen && "rotate(180deg)",
                          }),
                        }}
                        errorClass="error-msg error-msg-height"
                      />
                    </div>
                  </li>
                  <li className="text-center">
                    <span className="middle-separator">-</span>
                  </li>
                  <li className="position-relative">
                    <label className="d-none"></label>
                    <div ref={maxRef}>
                      <SelectDropDown
                        id={MAX_SF_DROPDOWN}
                        name="colors33"
                        options={squareFeetMaxList}
                        className="lotsize-select"
                        classNamePrefix="select"
                        placeholder="Select"
                        value={squareFootageMaxValue}
                        onChange={(e) => handleSquareFootMaxChange(e)}
                        onInputChange={(e) =>
                          handleOnInputChange(e, MAX_SF_DROPDOWN)
                        }
                        menuPlacement="top"
                        styles={{
                          dropdownIndicator: (provided, state) => ({
                            ...provided,
                            transform:
                              state.selectProps.menuIsOpen && "rotate(180deg)",
                          }),
                        }}
                        errorClass="error-msg error-msg-height"
                      />
                    </div>
                  </li>
                </ul>
              </div>
            </Accordion.Body>
          </Accordion.Item>
        )}
        {adType === "STR" && (
          <Accordion.Item eventKey="6">
            <Accordion.Header>Additional requirements</Accordion.Header>
            <Accordion.Body className="pt-0">
              <div className="field-wrap mt-0 mb-0">
                {loadAdditionalRequirements()}
              </div>
            </Accordion.Body>
          </Accordion.Item>
        )}
      </Accordion>

      <div className="mt-2">
        <a role="button" className="reset-btn" onClick={() => clearFilter()}>
          Clear all
        </a>
      </div>

      <PremiumPlanModal
        userDetails={userDetailsFromHook}
        show={showPremiumPlanModal}
        onHide={PremiumPlanModalClose}
        updateReloadParam={updateReloadParamAfterPlanChange}
        fromInventoryListing={false}
        showPlanModal={PremiumPlanModalShow}
      />
    </>
  );
};
export default LeftpanelTabContent;
